import {
  APP_ID,
  APP_INITIALIZER,
  LOCALE_ID,
  NgModule,
  Optional,
  PLATFORM_ID,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';

import { isPlatformBrowser, registerLocaleData } from '@angular/common';
import localeIt from '@angular/common/locales/it';

import { RouteReuseStrategy, Router } from '@angular/router';
import { CoreModule } from '@core/core.module';
import { CacheRouteReuseStrategy } from '@core/route-reuse.strategy';
import { AnalyticsDirective } from '@core/services/analytics.directive';
import { ApiService } from '@core/services/api.service';
import { ResourceHintsService } from '@core/services/resource-hints.service';
import { ScrollRestorationService } from '@core/services/scroll-restoration.service';
import * as Sentry from '@sentry/angular';
import { LanguageService } from '@shared/services/language.service';
import { TranslationLoaderService } from '@shared/services/translation.service';
import { BlockUIModule } from 'ng-block-ui';
import { BlockUIHttpModule } from 'ng-block-ui/http';
import { RECAPTCHA_V3_SITE_KEY } from 'ng-recaptcha';
import { environment } from 'src/environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';


registerLocaleData(localeIt);

import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  BrowserModule,
  provideClientHydration,
  withEventReplay,
  withHttpTransferCacheOptions,
} from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { FooterComponent } from '@core/components/footer/footer.component';
import { HeaderMobileComponent } from '@core/components/header-mobile/header-mobile.component';
import { HeaderComponent } from '@core/components/header/header.component';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import Aura from '@primeng/themes/aura';
import { CookieInterceptor } from '@shared/interceptors/cookie.interceptor';
import { SSR_ACCEPT_LANGUAGE, SSR_PATH } from '@shared/tokens/server.token';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { providePrimeNG } from 'primeng/config';
import { catchError, firstValueFrom, of } from 'rxjs';

export function getLanguage(languageService: LanguageService) {
  if (!languageService.getWindow()) {
    return 'it';
  }
  return languageService.getWindow().navigator.language == 'it'
    ? languageService.getWindow().navigator.language
    : 'en';
}

export function preloadTranslationsFactory(
  translate: TranslateService,
  languageService: LanguageService,
  platformId: object,
  ssrPath: string | null,
  ssrLang: string | null
) {
  return async () => {
    const pathLang =
      ssrLang ??
      ssrPath?.split('/')[1] ??
      (isPlatformBrowser(platformId)
        ? globalThis.location?.pathname?.split('/')[1]
        : undefined);

    const langFromPath = pathLang === 'it' || pathLang === 'en' ? pathLang : '';
    const defaultLanguage = langFromPath || 'en';

    let langCode = defaultLanguage;
    let langId = defaultLanguage === 'it' ? 1 : 2;

    if (isPlatformBrowser(platformId)) {
      const savedLangCode = localStorage.getItem('langCode');
      const savedLangId = Number.parseInt(localStorage.getItem('langId') ?? '', 10);
      const hasSavedLanguage = !!savedLangCode && !Number.isNaN(savedLangId);

      if (hasSavedLanguage) {
        if (langFromPath && savedLangCode !== defaultLanguage) {
          langCode = defaultLanguage;
          langId = defaultLanguage === 'it' ? 1 : 2;
          localStorage.setItem('langCode', langCode);
          localStorage.setItem('langId', String(langId));
        } else {
          langCode = savedLangCode as 'it' | 'en';
          langId = savedLangId;
        }
      } else {
        const browserLang = globalThis.navigator?.language?.toLowerCase() ?? 'en';
        if (defaultLanguage === 'it' || (!langFromPath && browserLang.startsWith('it'))) {
          langCode = 'it';
          langId = 1;
        }

        localStorage.setItem('langCode', langCode);
        localStorage.setItem('langId', String(langId));
      }
    }

    languageService.userSelectedLanguage = {
      idLanguage: langId,
      code: langCode,
      description: '',
    };

    if (translate.currentLang !== langCode) {
      translate.setDefaultLang(langCode);
      await firstValueFrom(translate.use(langCode).pipe(catchError(() => of({}))));
    }
  };
}

@NgModule({
  declarations: [AppComponent, AnalyticsDirective],
  imports: [
    BrowserModule,
    AppRoutingModule,
    CoreModule,
    BrowserAnimationsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: TranslationLoaderService,
        deps: [ApiService],
      },
    }),
    BlockUIModule.forRoot({
      delayStart: 180,
      delayStop: 10,
    }),
    BlockUIHttpModule.forRoot({
      requestFilters: [
        /geteventnotification|getcompetitions|getteams|getseasons|validate-shipping|getalltypes|getallv2|gethistory|geteventdateend|getauctionpriceinfo|getcountunreadmessages|get-current-prices|get-suggested|gettotalamount|memorabidevent\/getbypk\/|memorabidevent\/getbyslug/,
        /starredarticle|filter\/getalltypes|user\/ping|user\/getbyusername|reserve-prompt/,
        // Header/footer nav data and the i18n dictionary: fire unconditionally
        // on every page, so a slow response here reproduced the same "splash
        // screen keeps showing" symptom on any page, not just home.
        /genericpage\/getgroups|genericpage\/header-pages|genericpage\/footer-pages|translation\/getall/,
      ],
    }),
    HeaderComponent,
    HeaderMobileComponent,
    FooterComponent,
    // ServiceWorkerModule.register('ngsw-worker.js', {
    //   enabled: true,
    //   // Register the ServiceWorker as soon as the application is stable
    //   // or after 30 seconds (whichever comes first).
    //   registrationStrategy: 'registerWhenStable:30000',
    //   scope: '/',
    // }),
    ServiceWorkerModule.register('custom-sw.js', {
      enabled: true,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
      scope: '/',
    }),
  ],
  exports: [AnalyticsDirective],
  providers: [
    { provide: APP_ID, useValue: 'memorabid' },
    {
      provide: LOCALE_ID,
      //useValue: "it-IT"
      deps: [LanguageService],
      useFactory: getLanguage,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: RouteReuseStrategy,
      useClass: CacheRouteReuseStrategy,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: preloadTranslationsFactory,
      deps: [
        TranslateService,
        LanguageService,
        PLATFORM_ID,
        [new Optional(), SSR_PATH],
        [new Optional(), SSR_ACCEPT_LANGUAGE],
      ],
      multi: true,
    },
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useValue: environment.recaptcha.siteKey,
    },
    { provide: HTTP_INTERCEPTORS, useClass: CookieInterceptor, multi: true },
    provideClientHydration(
      withEventReplay(),
      withHttpTransferCacheOptions({
        includeRequestsWithAuthHeaders: true,
        filter: (a) => {
          const cachedUrls = [
            'genericpage',
            'publicgeneric',
            'entitydata/getbasedatabypk',
            'tag/getbyslug',
            'api/filter',
            'api/Translation/getalllanguages',
            'api/Translation/getall',
            'auction/getdefaultdata',
            'auctionView/get-suggested',
            'api/categories/related/by-tag-and-seller',
            'filter/getalltypes',
            'campaign/getbypk',
            'homePageCms',
            'getallpublicv2',
            'memorabidevent',
          ];

          if (a.method === 'GET') {
            const cached = cachedUrls.some((url) => a.url.includes(url));
            return cached;
          }
          return false;
        },
      })
    ),
    provideZoneChangeDetection({ eventCoalescing: true, runCoalescing: true }),
    provideEnvironmentNgxMask(),
    // Runs before the server serializes the document, so the hints ship with
    // the HTML rather than arriving after the bundle has already parsed.
    provideAppInitializer(() => inject(ResourceHintsService).add()),
    // Takes over window scrolling from the router (see ScrollRestorationService).
    provideAppInitializer(() => inject(ScrollRestorationService).init()),
    // Single root-level HttpClient configuration. `withInterceptorsFromDi()`
    // activates the class-based HTTP_INTERCEPTORS registered here and in
    // CoreModule; `withFetch()` is required for SSR.
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          dark: false,
          darkModeSelector: false,
        },
      },
    }),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
